const SESSION_KEY = "vv_chat_session";
const IS_DEV = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";

// Split "<followups>...</followups>" off the main body so we can render chips.
export function splitFollowups(text) {
  const m = text.match(/<followups>([\s\S]*?)<\/followups>/i);
  if (!m) return { body: text, followups: [] };
  const body = text.slice(0, m.index).trim();
  const followups = m[1]
    .split("\n").map((s) => s.replace(/^[-*•]\s*/, "").trim())
    .filter((s) => s && s.length < 100)
    .slice(0, 3);
  return { body, followups };
}

/**
 * Consume the SSE response body and dispatch delta chunks / session id / errors
 * back to the caller. Kept as a pure utility so <ChatWidget /> stays declarative.
 * Pass `signal` to allow the caller (e.g. resetChat) to abort mid-stream.
 */
export async function consumeChatStream(response, handlers, signal) {
  const { onSession, onDelta, onError } = handlers;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const abort = () => {
    try { reader.cancel(); } catch (err) {
      if (IS_DEV) console.warn("[chat] reader.cancel() failed", err);
    }
  };
  if (signal) {
    if (signal.aborted) { abort(); return; }
    signal.addEventListener("abort", abort, { once: true });
  }
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const raw of events) {
      const lines = raw.split("\n");
      const eventLine = lines.find((l) => l.startsWith("event:"));
      const dataLine  = lines.find((l) => l.startsWith("data:"));
      if (!eventLine || !dataLine) continue;
      const eventName = eventLine.slice(6).trim();
      // Per SSE spec: after "data:" strip AT MOST ONE space. Preserve the rest.
      let data = dataLine.slice(5);
      if (data.startsWith(" ")) data = data.slice(1);

      if (eventName === "session") {
        try { localStorage.setItem(SESSION_KEY, data); } catch (err) {
          if (IS_DEV) console.warn("[chat] failed to persist session", err);
        }
        onSession?.(data);
      } else if (eventName === "delta") {
        onDelta(data.replace(/\\n/g, "\n"));
      } else if (eventName === "error") {
        onError?.(data);
      }
    }
  }
}

export const CHAT_SESSION_KEY = SESSION_KEY;
